/**
 * Field toolkit client: Field Doctor (one-tap triage + repair), network tools, USB autopilot & baud detection.
 * Everything funnels through Bastet's generic `rpc` bridge, so the UI never needs per-feature native plumbing.
 */
import { BastetNative, isBastetNativeSupported } from './bastetBridge';

export type Severity = 'error' | 'warn' | 'info' | 'ok';

export interface DoctorFix {
  id: string;
  label: string;
  args?: Record<string, any>;
}

export interface DoctorFinding {
  id: string;
  severity: Severity;
  area: 'usb' | 'wifi' | 'bluetooth' | 'system' | 'policy' | string;
  title: string;
  detail: string;
  fix?: DoctorFix;
}

export interface DoctorScan {
  timestamp: number;
  score: number;
  summary: string;
  errors: number;
  warnings: number;
  scanMs: number;
  findings: DoctorFinding[];
  reportText: string;
  device?: { manufacturer: string; model: string; android: string; sdk: number; appVersion?: string };
  usb?: { deviceCount: number; devices: any[]; link?: any; autopilot?: AutopilotState; pluggedSource?: string };
  network?: Record<string, any>;
  bluetooth?: Record<string, any>;
  system?: Record<string, any>;
  policy?: { blockedRestrictions?: string[]; managedProfile?: boolean };
}

export interface DoctorFixResult {
  ok: boolean;
  message: string;
  launched?: boolean;
  needsShizuku?: boolean;
  detail?: any;
}

export interface AutopilotState {
  enabled: boolean;
  autoConnectSerial: boolean;
  autoConnectAdb: boolean;
}

export interface BaudDetectResult {
  ok: boolean;
  found?: boolean;
  bestBaud?: number;
  confidence?: number;
  durationMs?: number;
  hint?: string;
  error?: string;
  results?: Array<{ baud: number; supported?: boolean; bytes?: number; score?: number; sample?: string }>;
}

export interface QuickConnectResult {
  ok: boolean;
  kind?: 'serial' | 'adb';
  productName?: string;
  vendorIdHex?: string;
  productIdHex?: string;
  baudRate?: number;
  detection?: BaudDetectResult;
  error?: string;
  adb?: any;
}

export async function bastetRpc<T = any>(method: string, params: Record<string, any> = {}): Promise<T> {
  if (isBastetNativeSupported()) {
    const res = await (BastetNative as any).rpc({ method, params });
    return res?.result as T;
  }
  // Browser / dev preview fallback: only works when this page runs on the phone itself.
  const res = await fetch('http://127.0.0.1:8777/rpc', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: Date.now(), method, params }),
    signal: AbortSignal.timeout(30000)
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || 'RPC error');
  return data.result as T;
}

const errResult = (e: any): { ok: false; error: string } => ({ ok: false, error: e?.message || String(e) });

export const runDoctorScan = () => bastetRpc<DoctorScan>('doctor.scan');

export async function applyDoctorFix(fix: DoctorFix): Promise<DoctorFixResult> {
  try {
    return await bastetRpc<DoctorFixResult>('doctor.fix', { id: fix.id, args: fix.args || {} });
  } catch (e: any) {
    return { ok: false, message: e?.message || String(e) };
  }
}

export async function quickConnect(opts: { deviceId?: number; baudRate?: number; autoBaud?: boolean; dtr?: boolean; rts?: boolean } = {}): Promise<QuickConnectResult> {
  try {
    return await bastetRpc<QuickConnectResult>('usb.quick_connect', opts);
  } catch (e) {
    return errResult(e);
  }
}

export async function autoDetectBaud(opts: { candidates?: number[]; probe?: string; listen_ms?: number } = {}): Promise<BaudDetectResult> {
  try {
    return await bastetRpc<BaudDetectResult>('usb.auto_baud', opts);
  } catch (e) {
    return errResult(e);
  }
}

export async function getAutopilot(): Promise<AutopilotState | null> {
  try {
    return await bastetRpc<AutopilotState>('usb.autopilot', {});
  } catch {
    return null;
  }
}

export async function setAutopilot(p: Partial<{ enabled: boolean; autoConnect: boolean; autoAdb: boolean }>): Promise<AutopilotState | null> {
  try {
    return await bastetRpc<AutopilotState>('usb.autopilot', p);
  } catch {
    return null;
  }
}

// ───────────────────────── network toolbox ─────────────────────────

export const netPing = (host: string, count = 4) => bastetRpc<any>('net.ping', { host, count }).catch(errResult);
export const netDns = (host: string) => bastetRpc<any>('net.dns', { host }).catch(errResult);
export const netTcpCheck = (host: string, ports?: number[]) => bastetRpc<any>('net.tcp_check', { host, ports }).catch(errResult);
export const netHttpCheck = (url: string) => bastetRpc<any>('net.http_check', { url }).catch(errResult);
export const netProbe = () => bastetRpc<any>('net.probe').catch(errResult);

// ───────────────────────── helpers ─────────────────────────

export function parsePorts(text: string): number[] {
  return Array.from(
    new Set(
      text
        .split(/[\s,;]+/)
        .map((s) => parseInt(s, 10))
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 65535)
    )
  ).slice(0, 32);
}

export async function shareOrCopy(title: string, text: string): Promise<'shared' | 'copied' | 'failed'> {
  try {
    const nav: any = navigator;
    if (nav.share) {
      await nav.share({ title, text });
      return 'shared';
    }
  } catch {
    // user cancelled the share sheet, fall through to clipboard
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
